// @ts-nocheck
import _ from 'lodash';
import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { ArrowForward, ErrorOutline } from '@mui/icons-material';
import {
  Form,
  Formik,
  FormikConfig,
  FormikHelpers,
  FormikValues,
} from 'formik';
//
import { Api, getToken } from '@utils';
import { useAuthContext } from '@components/auth';
import store from '@store';
//
import FormikContext from './FormikContext';
import ObAlert from '@components/ObAlert';
import { useSnapshot } from 'valtio';
import { Spinner } from '@react-pdf-viewer/core';
import { ClockLoader, ScaleLoader } from 'react-spinners';

interface iFormikStep
  extends Pick<FormikConfig<FormikValues>, 'children' | 'validationSchema'> {
  label: string;
}

interface iFormikStepper extends FormikConfig<FormikValues> {
  children: React.ReactElement<iFormikStep>[] | React.ReactElement<iFormikStep>;
  error?: boolean;
  setError?: React.Dispatch<React.SetStateAction<boolean>>;
}

const FormikStepper: React.FC<iFormikStepper> = ({
  children,
  error,
  setError,
  ...props
}) => {
  const [initSteps, setInitSteps] = React.useState([]);
  const [businessType, setBusinessType] = React.useState('corporation');
  const [hasErr, setHasErr] = React.useState(false);
  const [isLast, setIsLast] = React.useState(false);

  const { initialValues: values } = props;

  // Get Valito State
  const state = useSnapshot(store);

  const { currentStep, setCurrentStep, steps, setSteps } =
    React.useContext(FormikContext);

  // console.log(hasErr);
  // console.log(state);
  // console.log({ isLast, hasErr, error });

  // Get business type
  const { obData } = useAuthContext();
  React.useEffect(() => {
    if (obData) {
      setBusinessType(obData.type);
    }
  }, [obData]);

  // console.log(steps);

  // Check for errors in steps array excluding the last step
  React.useEffect(() => {
    const _hasErr = steps
      .slice(0, steps.length - 1)
      .some((step) => step.hasError === true);

    setHasErr(_hasErr);
  }, [steps]);

  // Check if last step
  React.useEffect(() => {
    const _isLast = currentStep.step === steps.length - 1;
    setIsLast(_isLast);
  }, [currentStep, steps]);

  const stepsArray = React.Children.toArray(
    children
  ) as React.ReactElement<iFormikStep>[];

  const getSubSteps = stepsArray.map((child) => {
    // Filter based on child.props.type
    const _child = React.Children.toArray(child.props.children).filter(
      (step) => step.props.type === businessType || step.props.type === 'all'
    );

    // Filter based on child.props.notFor
    const _child2 = _child.filter((step) => step.props.notFor !== businessType);

    return _child2;
  });

  // Create subSteps array
  const createSubSteps = (index: number) => {
    if (getSubSteps[index]) {
      const _steps = getSubSteps[index].map((_step) => ({
        label: _step.props.label,
        isCompleted: false,
      }));

      return _steps;
    }
  };

  // Get the steps to local storage
  React.useEffect(() => {
    const _steps = localStorage.getItem('steps');
    if (_steps) {
      setInitSteps(JSON.parse(_steps));
    }
  }, []);

  // Create a new array of steps with label and isCompleted and subSteps if exists
  React.useEffect(() => {
    const _steps = stepsArray.map((_step, index) => ({
      label: _step.props.label,
      isCompleted: false,
      subSteps: createSubSteps(index) || [],
      hasError: false,
    }));

    // Check for initSteps
    if (initSteps.length > 0) {
      // Set isCompleted from initSteps
      _steps.forEach((_step, index) => {
        _step.isCompleted = initSteps[index].isCompleted;
        _step.subSteps.forEach((_subStep, subIndex) => {
          _subStep.isCompleted =
            initSteps[index].subSteps[subIndex].isCompleted;
        });
      });
    }

    // Set steps
    setSteps(_steps);
  }, [businessType, initSteps]);

  function useDeepCompareEffect(callback: () => void, dependencies: any[]) {
    const firstRun = React.useRef(true); // this ref is initialized only once and ignored in the next renders
    const prevDeps = React.useRef(dependencies);

    React.useEffect(() => {
      const isFirstRun = firstRun.current;
      const isSame = dependencies.every((dep, i) =>
        _.isEqual(dep, prevDeps.current[i])
      );

      if (isFirstRun || !isSame) {
        callback();
        callback(); // Run the callback a second time
      }

      firstRun.current = false;
      prevDeps.current = dependencies;
    });
  }

  // Usage
  useDeepCompareEffect(() => {
    if (initSteps && initSteps.length) {
      handleCheckSteps(initSteps);
    } else {
      // Create a new array of steps with label and isCompleted and subSteps if exists
      const _steps = stepsArray.map((_step, index) => ({
        label: _step.props.label,
        isCompleted: false,
        subSteps: createSubSteps(index) || [],
        hasError: false,
      }));

      // Set steps
      handleCheckSteps(_steps);
    }
  }, [obData, values, initSteps]);

  const currentStepEl = stepsArray[currentStep.step];
  const currentChild =
    getSubSteps[currentStep.step] &&
    getSubSteps[currentStep.step][currentStep.subStep];

  // Handle submit form
  const handleSubmitForm = async (
    values: FormikValues,
    helpers: FormikHelpers<FormikValues>
  ) => {
    // console.log(currentStep);
    // console.log(values);

    setBusinessType(values.type);

    const token = getToken();

    try {
      const { data } = await Api.put('/onboarding', values, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log(data);
      setError(false);
    } catch (error) {
      console.log(error);
    }

    // Call handleNext function
    await handleNext(values, helpers);
  };

  // Handle next step
  const handleNext = async (
    values: FormikValues,
    helpers: FormikHelpers<FormikValues>
  ) => {
    // Set current step isCompleted = true from steps array
    setSteps((prev) => {
      const _steps = [...prev];
      _steps[currentStep.step].subSteps[currentStep.subStep].isCompleted = true;
      return _steps;
    });

    // Set current step isCompleted = true from steps array to localStorage
    const _steps = JSON.parse(localStorage.getItem('steps')) || steps;

    _steps[currentStep.step].subSteps[currentStep.subStep].isCompleted = true;
    localStorage.setItem('steps', JSON.stringify(_steps));

    // check if all subSteps isCompleted = true from steps array
    const isAllSubStepsCompleted =
      steps[currentStep.step].subSteps.filter(
        (step) => step.isCompleted === false
      ).length === 0;

    // Set current step isCompleted = true from steps array
    if (isAllSubStepsCompleted) {
      setSteps((prev) => {
        const _steps = [...prev];
        _steps[currentStep.step].isCompleted = true;
        return _steps;
      });
      const _steps = JSON.parse(localStorage.getItem('steps'));
      _steps[currentStep.step].isCompleted = true;
      localStorage.setItem('steps', JSON.stringify(_steps));
    }

    if (currentStep.step === stepsArray.length - 1) {
      // Submit the onboarding form
      // await props.onSubmit(values, helpers);
      const isCompletedAllSteps =
        steps.filter((step) => step.isCompleted === false).length === 0 ||
        // skip the last field
        steps.filter((step) => step.isCompleted === false).length === 1;
      console.log({ steps });
      console.log({ isCompletedAllSteps });
      if (isCompletedAllSteps) {
        await props.onSubmit(values, helpers);
      } else {
        setError(true);
      }
    } else {
      if (currentStep.subStep === getSubSteps[currentStep.step].length - 1) {
        setCurrentStep({
          step: currentStep.step + 1,
          subStep: 0,
        });
      } else {
        if (currentStep.subStep <= getSubSteps[currentStep.step].length - 1) {
          setCurrentStep({
            step: currentStep.step,
            subStep: currentStep.subStep + 1,
          });

          // Set current step isCompleted = true from steps array
          setSteps((prev) => {
            const _steps = [...prev];
            _steps[currentStep.step].subSteps[currentStep.subStep].isCompleted =
              true;
            return _steps;
          });
          const _steps = JSON.parse(localStorage.getItem('steps'));
          _steps[currentStep.step].subSteps[currentStep.subStep].isCompleted =
            true;
          localStorage.setItem('steps', JSON.stringify(_steps));
        }
      }

      helpers.setTouched({});
    }
  };

  const handleCheckSteps = (s) => {
    let _newSteps = [...s];
    // Check business type
    if (values.country !== '' && values.type !== '') {
      _newSteps[0].subSteps[0].isCompleted = true;
    }

    // Check personal details
    if (
      values.type === 'sole' &&
      values.fName !== '' &&
      values.lName !== '' &&
      values.email !== '' &&
      values.birthday !== '' &&
      values.address !== '' &&
      values.city !== '' &&
      values.province !== '' &&
      values.postal !== ''
    ) {
      _newSteps[0].subSteps[1].isCompleted = true;
    }

    // Check Business details
    if (
      values.type === 'sole' &&
      values.businessName !== '' &&
      values.businessNumber !== '' &&
      values.website !== '' &&
      values.description !== ''
    ) {
      _newSteps[0].subSteps[2].isCompleted = true;
    }

    // Check business representative
    if (
      values.type !== 'sole' &&
      values.fName !== '' &&
      values.lName !== '' &&
      values.email !== '' &&
      values.birthday !== '' &&
      values.address !== '' &&
      values.city !== '' &&
      values.province !== '' &&
      values.postal !== ''
    ) {
      _newSteps[0].subSteps[2].isCompleted = true;
    }

    // Check for Business Details
    if (
      values.type !== 'sole' &&
      values.businessName !== '' &&
      values.businessNumber !== '' &&
      values.address !== '' &&
      values.city !== '' &&
      values.province !== '' &&
      values.postal !== '' &&
      values.industry !== '' &&
      values.website !== '' &&
      values.description !== ''
    ) {
      _newSteps[0].subSteps[1].isCompleted = true;
    }

    // Check for Public Details
    if (
      values.statementDescriptor !== '' &&
      values.supportPhone !== '' &&
      values.supportUrl !== '' &&
      values.supportEmail !== ''
    ) {
      _newSteps[0].subSteps[3].isCompleted = true;
    }

    // Check for Add your bank
    if (
      (values.institution !== '' &&
        values.transit !== '' &&
        values.account !== '' &&
        values.confirmAc !== '') ||
      obData.ac_ownership?.verified
    ) {
      _newSteps[1].subSteps[0].isCompleted = true;
    }

    // Check for Verify your documents
    // if (
    //   values.documents.idCard !== '' ||
    //   values.documents.driving !== '' ||
    //   values.documents.utility !== '' ||
    //   values.documents.vehicle !== ''
    // ) {
    //   _newSteps[2].subSteps[0].isCompleted = true;
    // }
    if (values.documents) {
      _newSteps[2].subSteps[0].isCompleted = true;
    }

    // Loop all the steps and Check for all the subSteps are completed
    _newSteps.forEach((_step, index) => {
      const isAllSubStepsCompleted =
        _step.subSteps.filter((step) => step.isCompleted === false).length ===
        0;

      if (isAllSubStepsCompleted) {
        _newSteps[index].isCompleted = true;
      }
    });

    localStorage.setItem('steps', JSON.stringify(_newSteps));
    setSteps(_newSteps);
    if (initSteps.length === 0) setInitSteps(_newSteps);
  };

  return (
    <Formik
      {...props}
      validationSchema={
        currentChild?.props?.validationSchema ||
        currentStepEl?.props?.validationSchema
      }
      onSubmit={handleSubmitForm}
    >
      {({ isSubmitting }) => (
        <Form autoComplete="off">
          {currentChild || currentStepEl}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              type="submit"
              style={{
                minWidth: '100%',
              }}
              disabled={isSubmitting || (isLast && (hasErr || error))}
            >
              {currentStep.step === stepsArray.length - 1
                ? 'Submit Application'
                : 'Continue'}{' '}
              {isSubmitting ? (
                <div style={{ marginLeft: 10 }}>
                  <ClockLoader size={20} color="#999" />
                </div>
              ) : (
                <ArrowForward fontSize="small" sx={{ ml: 1 }} />
              )}
            </Button>
          </Box>

          {isLast && (hasErr || error) && (
            <ObAlert type="info" mt={2}>
              <Typography variant="body2">
                Somethings wrong. Please check the form again.
              </Typography>
            </ObAlert>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default FormikStepper;
